import { apiClient } from "./api";
import {
  User,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  RefreshTokenResponse,
  ForgotPasswordData,
  ResetPasswordData,
  VerifyEmailData,
  UpdateProfileData,
} from "../types/auth";

export const authService = {
  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(
      "/auth/login",
      credentials,
      { timeout: 60000 }
    );
    return response.data.data;
  },

  // Register user
  async register(data: RegisterData): Promise<void> {
    await apiClient.post("/auth/register", data);
  },

  // Refresh access token
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>(
      "/auth/refresh",
      {
        refreshToken,
      }
    );
    return response.data.data;
  },

  // Logout user
  async logout(): Promise<void> {
    await apiClient.post("/auth/logout");
  },

  // Get current user
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<{ user: User }>("/me");
    return response.data.data.user;
  },

  // Update user profile
  async updateProfile(data: UpdateProfileData): Promise<User> {
    const response = await apiClient.patch<{ user: User }>("/me", data);
    return response.data.data.user;
  },

  // Forgot password
  async forgotPassword(data: ForgotPasswordData): Promise<void> {
    await apiClient.post("/auth/forgot-password", data);
  },

  // Reset password
  async resetPassword(data: ResetPasswordData): Promise<void> {
    await apiClient.post("/auth/reset-password", data);
  },

  // Verify email
  async verifyEmail(data: VerifyEmailData): Promise<void> {
    await apiClient.post("/auth/verify-email", data);
  },

  // Resend verification email
  async resendVerificationEmail(email: string): Promise<void> {
    await apiClient.post("/auth/resend-verification", { email });
  },

  // Change password
  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<void> {
    await apiClient.post("/auth/change-password", data);
  },

  // Delete account
  async deleteAccount(password: string): Promise<void> {
    await apiClient.delete("/me", {
      data: { password },
    });
  },

  // Get user sessions
  async getSessions(): Promise<any[]> {
    const response = await apiClient.get("/me/sessions");
    return response.data.data;
  },

  // Revoke session
  async revokeSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/me/sessions/${sessionId}`);
  },

  // Revoke all sessions except current
  async revokeAllSessions(): Promise<void> {
    await apiClient.delete("/me/sessions");
  },
};
